/**
 * IWINV Template Contract Implementation  
 */

import {
  TemplateContract,
  TemplateCreateRequest,
  TemplateUpdateRequest,
  TemplateCreateResult,
  TemplateUpdateResult,
  ProviderTemplate,
  TemplateFilters,
  SyncResult,
  TemplateStatus
} from '../../contracts/provider.contract';

import { IWINVConfig } from '../types/iwinv';

export class IWINVTemplateContract implements TemplateContract {
  constructor(private config: IWINVConfig) {}

  async create(template: TemplateCreateRequest): Promise<TemplateCreateResult> {
    try {
      const response = await fetch(`${this.config.baseUrl}/template/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          templateName: template.name,
          templateContent: template.content,
          templateCategory: template.category,
          templateVariables: template.variables,
          templateButtons: template.buttons
        })
      });

      const result = await response.json() as Record<string, unknown>;

      if (!response.ok) {
        throw new Error(`Template creation failed: ${(result.message as string)}`);
      }

      return {
        templateId: (result.templateId as string) || `tpl_${Date.now()}`,
        providerTemplateCode: (result.templateCode as string) || template.name,
        status: TemplateStatus.PENDING,
        message: (result.message as string) || 'Template created successfully'
      };
    } catch (error) {
      throw new Error(`Failed to create template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async update(templateId: string, template: TemplateUpdateRequest): Promise<TemplateUpdateResult> {
    try {
      const response = await fetch(`${this.config.baseUrl}/template/modify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          templateCode: templateId,
          templateName: template.name,
          templateContent: template.content,
          templateButtons: template.buttons
        })
      });

      const result = await response.json() as Record<string, unknown>;

      if (!response.ok) {
        throw new Error(`Template update failed: ${(result.message as string)}`);
      }

      return {
        templateId,
        status: TemplateStatus.PENDING,
        message: (result.message as string) || 'Template updated successfully'
      };
    } catch (error) {
      throw new Error(`Failed to update template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async delete(templateId: string): Promise<void> {
    try {
      const response = await fetch(`${this.config.baseUrl}/template/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          templateCode: templateId
        })
      });

      const result = await response.json() as Record<string, unknown>;

      if (!response.ok) {
        throw new Error(`Template deletion failed: ${(result.message as string)}`);
      }
    } catch (error) {
      throw new Error(`Failed to delete template: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async get(templateId: string): Promise<ProviderTemplate> {
    interface IWINVTemplateFilters extends TemplateFilters {
      templateCode?: string;
      templateName?: string;
      templateStatus?: string;
    }
    const templates = await this.list({ templateCode: templateId } as IWINVTemplateFilters);
    const template = templates.find(t => t.code === templateId);
    
    if (!template) {
      throw new Error(`Template ${templateId} not found`);
    }

    return template;
  }

  async list(filters?: TemplateFilters): Promise<ProviderTemplate[]> {
    try {
      const response = await fetch(`${this.config.baseUrl}/template/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          page: 1,
          size: 100,
          ...filters
        })
      });

      const result = await response.json() as Record<string, unknown>;

      if (!response.ok) {
        throw new Error(`Failed to list templates: ${(result.message as string)}`);
      }

      return ((result.list as any[]) || []).map((template: any) => ({
        id: template.templateId || template.templateCode,
        code: template.templateCode,
        name: template.templateName,
        content: template.templateContent,
        status: this.mapIWINVStatus(template.status || template.templateStatus),
        createdAt: template.createDate ? new Date(template.createDate) : new Date(),
        updatedAt: template.updateDate ? new Date(template.updateDate) : new Date(),
        approvedAt: template.approvedAt ? new Date(template.approvedAt) : undefined,
        rejectedAt: template.rejectedAt ? new Date(template.rejectedAt) : undefined,
        rejectionReason: template.rejectionReason
      }));
    } catch (error) {
      throw new Error(`Failed to list templates: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async sync(): Promise<SyncResult> {
    try {
      const templates = await this.list();
      
      return {
        synced: templates.length,
        created: 0,
        updated: 0,
        deleted: 0,
        errors: []
      };
    } catch (error) {
      return {
        synced: 0,
        created: 0,
        updated: 0,
        deleted: 0,
        errors: [{
          templateId: 'unknown',
          error: error instanceof Error ? error.message : 'Sync failed'
        }]
      };
    }
  }

  private mapIWINVStatus(status: string): TemplateStatus {
    switch (status) {
      case 'Y':
        return TemplateStatus.APPROVED;
      case 'I':
        return TemplateStatus.PENDING;
      case 'R':
        return TemplateStatus.REJECTED;
      case 'D':
        return TemplateStatus.DISABLED;
      default:
        return TemplateStatus.DRAFT;
    }
  }
}